import { Survey, SurveyResponse } from "src/app/handweave-api";
import { AlertDefaults, AlertSettings, SurveySettings, SurveySummary } from "src/app/hickory-api";
import { AlertModelService } from "../alert-model.service";
import { AlertDefaultsHelper } from "..";
import { AvailableTemplatesService } from "src/app/shared/templated-input/available-templates.service";


export class AlertSurveyHelper {
  // avoid circular dependency by not importing AlertFields
  static readonly FIELD_NAME_SENDER = 'surveySender';
  static readonly FIELD_NAME_RECIPIENT = 'surveyRecipient';

  static validateSender(service: AlertModelService, survey: SurveySettings = service.alertSurvey): string[] {
    if (service.isEditingTemplate) {
      return survey?.sender?.survey?.id ? [] : ['SelectSurveySender'];
    } else {
      return survey?.sender?.response?.id ? [] : ['FinishSurveySender'];
    }
  }

  static validateRecipient(service: AlertModelService, survey: SurveySettings = service.alertSurvey): string[] {
    return survey?.recipient?.survey?.id ? [] : ['SelectSurveyRecipient'];
  }

  static preparePreview(service: AlertModelService, preview: AlertDefaults) {
    // when saving a template, make sure that we remove any response from the surveySender summary
    if (service.isEditingTemplate && !!preview.message?.survey?.sender?.response?.id) {
      preview.message.survey.sender.response = undefined;
    }
  }

  /**
   * Applies a Survey and/or SurveyResponse model from Handweave to the working AlertDefaults model
   * on our AlertModelService, then notifies listeners of changes to the field.
   */
  static applySurveySelection(service: AlertModelService, selection: Survey, response: SurveyResponse, fieldName: string, placeholderService: AvailableTemplatesService = null) {
    const alert = service?.alertSettings;
    // if all of these are null, this is a no-op so early exit
    if (!alert?.message?.survey && !selection && !response) {
      return;
    }
    const summary = this.buildSurveySummary(selection, response);
    this.applyToAlert(alert, summary, fieldName);
    service.notifyFieldUpdated(fieldName);

    if (placeholderService) {
      this.handleSurveyPlaceholders(service, placeholderService);
    }
  }

  static handleSurveyPlaceholders(alertModel: AlertModelService, placeholderService: AvailableTemplatesService) {
    const surveyId = alertModel.alertMessage?.survey?.sender?.survey?.id;
    const responseId = alertModel.alertMessage?.survey?.sender?.response?.id;

    if (surveyId && responseId) {
      placeholderService.setSurveyInfo({ surveyId, responseId });
    }
  }

  static handleAlertSurveyPlaceholders(alertSettings: AlertSettings, placeholderService: AvailableTemplatesService) {
    const alertSurveyId = alertSettings.message?.survey?.sender?.survey?.id;
    const alertResponseId = alertSettings.message?.survey?.sender?.response?.id;

    if (alertSurveyId && alertResponseId) {
      placeholderService.setSurveyInfo({ surveyId: alertSurveyId, responseId: alertResponseId });
    }
  }

  static buildSurveySummary(selection: Survey, response: SurveyResponse): SurveySummary {
    let summary: SurveySummary = undefined;
    if (selection?.id) {
      summary = {
        survey: {
          id: selection.id,
          uri: selection['uri'],
          name: selection.name,
          description: selection.description,
        },
        response: undefined,
      };
      if (response?.id) {
        summary.response = {
          id: response.id,
          uri: response['uri'],
          name: response.name,
          description: undefined,
        };
      }
    }
    return summary;
  }

  static applyToAlert(alert: AlertDefaults, summary: SurveySummary, fieldName: string) {
    if (!alert) {
      return;
    }

    // ensure MessageSettings model exists
    AlertDefaultsHelper.initDefaults(alert);

    fieldName = fieldName ?? this.FIELD_NAME_SENDER;
    const surveySettings = alert.message.survey ?? { };
    if (fieldName === this.FIELD_NAME_RECIPIENT) {
      surveySettings.recipient = summary;
    } else {
      surveySettings.sender = summary;
    }
    alert.message.survey = surveySettings;
  }

  static surveyIcon(forRecipient: boolean = false): string {
    return forRecipient ? 'equalizer' : 'dynamic_form';
  }

}